"use client";

import { useEffect } from "react";
import useAfterLCP from "@/utils/hooks/useAfterLCP";

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];
// Upper bound so PageView still fires for visitors who never interact.
const FALLBACK_MS = 6000;

function loadPixel(pixelId: string) {
  const w = window as any;
  if (w.fbq) return; // already installed (e.g. by a GTM tag) — never double-fire

  // Standard Meta stub: queues calls until fbevents.js loads.
  const fbq: any = function (...args: any[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  w.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  const script = document.createElement("script");
  script.async = true;
  script.src = FBEVENTS_SRC;
  document.head.appendChild(script);

  // Must precede init. Turns off fbevents.js's automatic button-click /
  // page-metadata listeners, which add a heavy document click handler on every
  // interaction (INP / presentation delay). The explicit PageView is unaffected.
  fbq("set", "autoConfig", false, pixelId);
  fbq("init", pixelId);
  fbq("track", "PageView");
}

/**
 * Meta (Facebook) Pixel, loaded off the critical path.
 *
 * Gate order: LCP recorded -> first user interaction (or FALLBACK_MS cap) ->
 * main thread idle. Plain DOM injection (no next/script). Disabled unless
 * NEXT_PUBLIC_META_PIXEL_ID is set.
 *
 * NOTE: if the pixel is also fired by a GTM tag, remove that tag first — the
 * `window.fbq` guard stops a second install, but not a second PageView from
 * GTM's own copy.
 */
export function MetaPixel() {
  const lcpDone = useAfterLCP();

  useEffect(() => {
    const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;
    if (!pixelId || !lcpDone) return;

    let fired = false;
    let idleId: number | undefined;

    const detach = () =>
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, trigger));

    function trigger() {
      if (fired) return;
      fired = true;
      detach();
      clearTimeout(fallbackId);
      const run = () => loadPixel(pixelId as string);
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(run, { timeout: 2000 });
      } else {
        idleId = window.setTimeout(run, 200);
      }
    }

    INTERACTION_EVENTS.forEach((e) =>
      window.addEventListener(e, trigger, { once: true, passive: true }),
    );
    const fallbackId = setTimeout(trigger, FALLBACK_MS);

    return () => {
      detach();
      clearTimeout(fallbackId);
      if (idleId !== undefined) {
        if (typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleId);
        } else {
          clearTimeout(idleId);
        }
      }
    };
  }, [lcpDone]);

  return null;
}

/** Place inside <body>; only the no-JS fallback image. */
export function MetaPixelNoScript() {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;
  if (!pixelId) return null;
  return (
    <noscript>
      <img
        height="1"
        width="1"
        style={{ display: "none" }}
        alt=""
        src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
      />
    </noscript>
  );
}
